import { useMemo } from "react";
import type { ItemTypeMetadata } from "../types";

interface UseKanbanGroupingOptions {
  metadata: ItemTypeMetadata | undefined;
}

interface UseKanbanGroupingResult {
  /** Whether kanban view is available (has lifecycle map) */
  isKanbanAvailable: boolean;
  /** The lifecycle map ID (for fetching states) */
  lifecycleMapId: string | null;
  /** camelCase field name for grouping — always "state" for lifecycle-based kanban */
  groupByField: "state" | null;
  /** snake_case field name — always "state" */
  groupByFieldSnake: "state" | null;
}

/**
 * Hook to determine if Kanban view is available for an ItemType.
 *
 * Kanban is only available when the ItemType has a Life Cycle Map.
 * Columns are always based on lifecycle states, grouped by the "state" field.
 */
export const useKanbanGrouping = ({
  metadata,
}: UseKanbanGroupingOptions): UseKanbanGroupingResult => {
  return useMemo(() => {
    const mapId = metadata?.lifecycleMapId;

    if (!mapId) {
      return {
        isKanbanAvailable: false,
        lifecycleMapId: null,
        groupByField: null,
        groupByFieldSnake: null,
      };
    }

    return {
      isKanbanAvailable: true,
      lifecycleMapId: mapId,
      groupByField: "state",
      groupByFieldSnake: "state",
    };
  }, [metadata?.lifecycleMapId]);
};
